import { useCallback } from "react";
import { useHistory } from "react-router";
import {
  ContactInformation,
  ContactSelectors,
  useCheckoutStateSelector as useSelector,
  useCheckoutSend,
  ContactEventType,
  type IContactInfo,
  FlightSelectors,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { removeLocalStorageTrackingEvents } from "../../../hooks/useFlightLocalStorageTracking";
import { logContactContinueRequested } from "../contactInformationInstrumentation";
import type { TEvent } from "../events";
import { getClientAssets, getStateValue } from "../selectors";
import { ConnectedBreadcrumbs } from "./Breadcrumbs";
import { ConnectedPriceBreakdownContent } from "./PriceBreakdownDropdown";

export const nubankPhoneRegex = /\(?\d{2}\)?\s?9\d{4}[- ]?\d{4}$/;

export const ConnectedContactInformation = (props) => {
  const { t, brand } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();

  const step = useSelector(getStateValue);
  const clientAssets = useSelector(getClientAssets);
  const localStorageTrackingEventsKey = useSelector(
    FlightSelectors.getLocalStorageKey
  );

  const contactInfo = useSelector(ContactSelectors.getContactInformation);

  const handleContinue = useCallback(() => {
    logContactContinueRequested({
      stateValue: step,
      hasEmail: Boolean(contactInfo?.email),
      hasPhoneNumber: Boolean(contactInfo?.phoneNumber),
      hasCountryCode: Boolean(contactInfo?.countryCode),
    });
    send(ContactEventType.NEXT);
  }, [contactInfo, send, step]);

  const handleGoBack = () => {
    // clear local storage since we're going back to shop
    removeLocalStorageTrackingEvents(localStorageTrackingEventsKey);
    history.goBack();
  };

  const onContactInfoChange = useCallback(
    (newContactInfo: IContactInfo) =>
      send({ type: ContactEventType.CHANGE, contactInfo: newContactInfo }),
    [send]
  );

  return (
    <ContactInformation
      {...props}
      contactInfo={contactInfo}
      onGoBack={handleGoBack}
      onContinueClick={handleContinue}
      onContactInfoChange={onContactInfoChange}
      className="b2b"
      fullScreenWithBanner={true}
      desktopRightContent={<ConnectedPriceBreakdownContent />}
      desktopTopContent={<ConnectedBreadcrumbs />}
      clientAssets={clientAssets}
      step={step}
      headerTitle={t("contactInformation")}
      headerElement={t("contactInformation")}
      phoneRegex={
        contactInfo.countryCode === brand.preferredAreaCode
          ? nubankPhoneRegex
          : undefined
      }
      checkPhoneMinLength
      minPhoneLength={9}
    />
  );
};
